import { useCallback, useEffect, useState } from "react";
import { deleteJobs, fetchStatus } from "../queue";
import { JOBS_DELETED_EVENT, STATUS_ADDITIONAL_DATA_EVENT, STATUS_EVENT } from "../types";
import { useStatusErrors } from "./useStatusErrors";
function useProgress(param) {
    let { type, fetchStatusInterval, fetchAdditionalData = false, onAdditionalData, onCancel } = param;
    const [cancelling, setCancelling] = useState(false);
    const [jobsStarted, setJobsStarted] = useState(false);
    const [remaining, setRemaining] = useState();
    const [percent, setPercent] = useState();
    const [total, setTotal] = useState();
    const [failure, setFailure] = useState();
    const [paused, setPaused] = useState();
    const [currentJob, setCurrentJob] = useState();
    const { errors: { [type]: errors } } = useStatusErrors();
    // Determine status
    let status = "not-started";
    if (jobsStarted) {
        if (errors) {
            status = "failed";
        } else if (remaining === 0 && !failure) {
            status = "done";
        }
    }
    // Listen to status updates
    const listener = useCallback(async (param)=>{
        let { detail: { currentJobs, remaining: { [type]: remainingArr }, additionalData } } = param;
        if (remainingArr) {
            const { remaining, total, failure, paused } = remainingArr;
            const percent = +((total - remaining) / total * 100).toFixed(0);
            setCurrentJob(currentJobs[type]);
            setRemaining(remaining);
            setTotal(total);
            setFailure(failure);
            setPaused(paused);
            setPercent(percent > 0 ? percent : 1);
        } else {
            setCurrentJob(undefined);
            setRemaining(0);
            setTotal(0);
            setFailure(0);
            setPaused(0);
            setPercent(100);
        }
        // Always handle the received response, too
        if (fetchAdditionalData !== false && additionalData[fetchAdditionalData]) {
            onAdditionalData(additionalData);
        }
    }, [
        type,
        onAdditionalData,
        fetchAdditionalData
    ]);
    // Listen to all status updated
    useEffect(()=>{
        document.addEventListener(STATUS_EVENT, listener);
        return ()=>{
            document.removeEventListener(STATUS_EVENT, listener);
        };
    }, [
        listener
    ]);
    useEffect(()=>{
        if (remaining > 0) {
            setJobsStarted(true);
        }
    }, [
        remaining
    ]);
    // Let the status updater know that we want to fetch additional data
    useEffect(()=>{
        if (fetchAdditionalData) {
            const listener = async (param)=>{
                let { detail: { settings: { additionalData } } } = param;
                additionalData.push(fetchAdditionalData);
            };
            document.addEventListener(STATUS_ADDITIONAL_DATA_EVENT, listener);
            return ()=>{
                document.removeEventListener(STATUS_ADDITIONAL_DATA_EVENT, listener);
            };
        }
        return ()=>{
        // Silence is golden.
        };
    }, [
        fetchAdditionalData
    ]);
    const applyCancelState = useCallback(()=>{
        setJobsStarted(false);
        setCurrentJob(undefined);
        setRemaining(0);
        setTotal(0);
        setFailure(0);
        setPaused(0);
        setPercent(100);
        onCancel == null ? void 0 : onCancel();
    }, [
        onCancel
    ]);
    // Listen to manual deletion of tasks in the error modal
    useEffect(()=>{
        const listenerDeleted = async (param)=>{
            let { detail: { params: { type: deletedType } } } = param;
            if (deletedType === type) {
                applyCancelState();
            }
        };
        document.addEventListener(JOBS_DELETED_EVENT, listenerDeleted);
        return ()=>{
            document.removeEventListener(JOBS_DELETED_EVENT, listenerDeleted);
        };
    }, [
        listener,
        applyCancelState
    ]);
    const handleCancel = useCallback(async ()=>{
        applyCancelState();
        setCancelling(true);
        await deleteJobs({
            type
        });
        setCancelling(false);
    }, [
        type,
        applyCancelState
    ]);
    // Modify the status updater
    useEffect(()=>{
        if (fetchStatusInterval > 0) {
            fetchStatus(fetchStatusInterval, true);
        }
        return ()=>{
            fetchStatus();
        };
    }, [
        fetchStatusInterval
    ]);
    return {
        errors,
        status,
        jobsStarted,
        remaining,
        percent,
        total,
        failure,
        paused,
        currentJob,
        handleCancel,
        cancelling
    };
}
export { useProgress };
